import React from 'react';
import './UnifiedSocialCanvas.css';

const UnifiedSocialCanvas = ({
  // Content props
  mainHeading = 'Job Title',
  company = '',
  location = '',
  topTitle = '',
  taglineText = '',
  ctaText = '',
  supportingText = '',

  // Display toggles
  showChips = true,
  showTopTitle = false,
  showTagline = false,
  showCta = false,
  showSupportingText = false,

  // Typography
  headingSize = 24,
  textColor = '#ffffff',
  bodySize = 12,
  padSize = 30,

  // Background
  backgroundImage = null,
  backgroundSize = 100,
  backgroundPositionX = 50,
  backgroundPositionY = 50,
  gradientOverlay = 'linear-gradient(135deg, #4f46e5, #9333ea, #0ea5e9)',
  // Overlay opacity (0-100)
  backgroundOverlayOpacity = 28,

  // Logo
  showLogo = false,
  logoUrl = '',
  logoSize = 42,
  // Logo position (px from left/top)
  logoX = 20,
  logoY = 12,
  // Force white logo (UI toggle)
  forceWhiteLogo = true,
  // Canvas ID
  canvasId = 'social-canvas',

  // Salary (restored)
  showSalary = false,
  salary = ''
}) => {
  const canvasStyle = {
    // Always use gradient as the base background — the image is rendered as an
    // <img> element below so dom-to-image can capture it reliably on export.
    background: gradientOverlay,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    border: 'none',            // force no border on export
    outline: 'none',
    boxShadow: 'none',         // avoid export library adding a hairline shadow/border
    overflow: 'hidden',        // clip the bg img to the canvas bounds
    '--heading-size': `${headingSize}px`,
    '--body-size': `${bodySize}px`,
    '--pad-size': `${padSize}px`,
    '--element-spacing': '16px',
    '--line-height': '0.9',
    '--letter-spacing': '-0.02em',
    '--content-width': '100%',
    // expose overlay alpha as a CSS custom property (0-1 mapped from 0-200 slider)
    '--overlay-alpha': `${Math.min(1, backgroundOverlayOpacity / 200).toFixed(3)}`,
    '--text-color': textColor || '#ffffff'
  };


  return (
    <div className="unified-canvas-container">
      <div 
          id={canvasId}
          className="unified-canvas"
          style={canvasStyle}
          data-force-white-logo={forceWhiteLogo ? '1' : '0'}
        >
  {/* Background image rendered as <img> so dom-to-image captures it correctly */}
  {backgroundImage && (
    <img
      src={backgroundImage}
      crossOrigin="anonymous"
      alt=""
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: `${backgroundPositionX}% ${backgroundPositionY}%`,
        // Scale min 1.0 so the image always fills the canvas — values above 1 zoom in
        transform: `scale(${Math.max(1, backgroundSize / 100)})`,
        transformOrigin: `${backgroundPositionX}% ${backgroundPositionY}%`,
        zIndex: 1,
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  )}
  {/* Visual overlay to improve contrast when background image is present */}
  <div className="canvas-overlay" style={{ background: `rgba(0,0,0,${Math.min(1, backgroundOverlayOpacity / 200).toFixed(3)})`, zIndex: 2 }} />
        {/* Canvas inner - EXACT match HTML structure */}
  <div className="canvas-inner" style={{ position: 'relative', zIndex: 10 }}>
          {/* Top title - EXACT match HTML */}
          {showTopTitle && topTitle && (
            <h2 id="topTitle" className="top-title" style={{ display: showTopTitle ? 'block' : 'none' }}>
              {topTitle}
            </h2>
          )}
          {/* Main heading - EXACT match HTML h3 */}
          <h3
            id="mainHeading"
            className="main-heading"
            style={{
              whiteSpace: 'normal',            // allow wrapping (fixes cut-off)
              overflow: 'visible',
              textOverflow: 'clip',
              wordBreak: 'break-word',
              lineHeight: 'var(--line-height)'
            }}
          >
            {mainHeading}
          </h3>
          {/* Company and location pills - EXACT match HTML chip-row structure */}
          {showChips && (company || location) && (
            <div className="chip-row" id="chipRow">
              {company && (
                <div className="chip" id="companyChip">
                  {company}
                </div>
              )}
              {location && (
                <div className="chip" id="locationChip">
                  {location}
                </div>
              )}
            </div>
          )}
          {/* Supporting text - EXACT match HTML */}
          {showSupportingText && supportingText && (
            <p
              className="supporting-text"
              id="supportingText"
              style={{
                display: showSupportingText ? 'block' : 'none',
                whiteSpace: 'normal',
                overflow: 'visible'
              }}
            >
              {supportingText}
            </p>
          )}
        </div>
        {/* Salary badge (moved outside canvas-inner so it isn't clipped by sibling stacking contexts) */}
        {showSalary && salary && (
          <div
            id="salaryBadge"
            className="salary-badge"
            style={{
              position: 'absolute',
              top: '12px',
              right: '20px',
              color: '#fff',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            }}
          >
            {salary}
          </div>
        )}
        {/* Tagline - EXACT match HTML positioning */}
        {showTagline && taglineText && (
          <div className="tagline" id="taglineText" style={{ display: showTagline ? 'block' : 'none' }}>
            {taglineText}
          </div>
        )}
        {/* Bottom bar with CTA - EXACT match HTML structure */}
        <div className="bottom-bar">
          {showCta && ctaText && (
            <div className="cta" id="ctaButton">
              {ctaText}
            </div>
          )}
        </div>
        {/* Logo - Placed at top left */}
        {showLogo && logoUrl && (
          <img 
            src={logoUrl} 
            alt="Brand Logo"
            className={`brand-logo ${forceWhiteLogo ? 'brand-logo--white' : ''}`}
            style={{
              position: 'absolute',
              top: `${logoY}px`,
              left: `${logoX}px`,
              height: `${logoSize}px`,
              width: 'auto',
              zIndex: 100
            }}
          />
        )}
      </div>
    </div>
  );
};

export default UnifiedSocialCanvas;
